import * as Sentry from "@sentry/react";
import type { Row } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useRealtime } from "@/features/realtime-api/context-provider";
import { bookingTextPrompt } from "@/lib/prompts/booking-text";
import { serviceResearchPrompt } from "@/lib/prompts/service";
import { cn } from "@/lib/utils";
import { useGetCompanyInfo } from "@/queries/companies-info/fetch";
import { useBookingTextStore } from "@/queries/fastlane/get-review";
import { useUpdateManyLineItems } from "@/queries/line-items/update";

import {
  AssistantToolbar,
  AssistantToolbarRoot,
  AssistantToolbarTrigger,
} from "@/components/shared/toolbars/assistant-toolbar";
import { AssistantToolbarAskAi } from "@/components/shared/toolbars/assistant-toolbar";
import { TextInputCell } from "./text-input-cell";

type Props = {
  description: string;
  invoiceText: string;
  invoiceIntent: string;
  rowId: string;
  subRows: Row<any>[];
  depth: number;
  toolbarDisabled?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onChange: (lineItemIds: string[], text: string) => Promise<void> | void;
  disabled?: boolean | string | React.ReactNode;
  readOnly?: boolean | string | React.ReactNode;
};

export const BookingTextCell = ({
  description,
  invoiceText,
  invoiceIntent,
  rowId,
  subRows,
  readOnly,
  depth,
  className,
  toolbarDisabled,
  disabled,
  onChange,
}: Props) => {
  const { companyInfo } = useGetCompanyInfo();
  const { sendRequest } = useRealtime();

  const updateLineItems = useUpdateManyLineItems();
  const { getBookingText, setBookingText, bookingTexts } =
    useBookingTextStore();
  const { t } = useTranslation();

  const setBookingLoadingBookingText = useCallback(
    (rowsIds: string[]) => {
      setBookingText(rowsIds.join("-"), {
        lineItemIds: rowsIds,
        bookingText: "loading",
      });
    },
    [setBookingText],
  );

  const handleRefreshBookingText = async (
    rowsIds: string[],
    lineItemNames: string,
  ): Promise<string> => {
    let modelOutput = "";
    setBookingLoadingBookingText(rowsIds);

    try {
      const prompt = bookingTextPrompt({
        lineItemNames,
        invoiceInterpretation: invoiceIntent,
      });

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          Sentry.captureMessage("Booking text generation timed out", {
            extra: {
              rowsIds,
            },
          });
          reject(new Error("Booking text generation timed out"));
        }, 5000);
      });

      const {
        item: { content },
      } = await Promise.race([sendRequest(prompt.human), timeoutPromise]);

      const [message] = content;
      const { text } = message;
      modelOutput = message;

      const cleanedJson = text.replace(/```json|```/g, "").trim();
      const parsedData = JSON.parse(cleanedJson);

      if (!parsedData.summary?.trim())
        throw new Error(`No booking text generated ${text}`);

      setBookingText(rowsIds.join("-"), {
        lineItemIds: rowsIds,
        bookingText: parsedData.summary,
      });

      return parsedData.summary;
    } catch (e) {
      Sentry.captureException(e, {
        extra: { modelOutput: JSON.stringify(modelOutput) },
      });
      setBookingText(rowsIds.join("-"), {
        lineItemIds: rowsIds,
        bookingText: lineItemNames,
      });
      return lineItemNames;
    }
  };

  useEffect(() => {
    const fetchBookingText = async () => {
      if (subRows.length === 0) return;

      const rowsIds =
        subRows.length > 0
          ? subRows.map((subRow) => subRow.original.id)
          : [rowId];

      const cachedText = getBookingText(rowsIds.join("-"));

      const lineItemNames = subRows
        .map((subRow) => subRow.original.originalName)
        .join(", ");

      let fetchedText = cachedText?.bookingText;
      let reGeneratedText = false;

      if (!cachedText) {
        fetchedText = await handleRefreshBookingText(rowsIds, lineItemNames);

        if (!fetchedText) {
          Sentry.captureMessage("No booking text refreshed", {
            extra: {
              rowsIds,
            },
          });
        }

        reGeneratedText = true;
      }

      return { fetchedText, reGeneratedText };
    };

    fetchBookingText().then(async (res) => {
      if (!res?.fetchedText || !res?.reGeneratedText) return;

      const rowsIds = subRows.map((subRow) => subRow.original.id);

      await updateLineItems(rowsIds, {
        bookingText: res.fetchedText,
        bookingTextManuallyUpdated: false,
      });
    });
  }, [rowId, subRows, getBookingText, setBookingText]);

  const bookingText = useMemo(() => {
    if (depth === 1) return description;
    const rowsIds = subRows.map((subRow) => subRow.original.id);
    const cachedText = getBookingText(rowsIds.join("-"));
    return cachedText?.bookingText ?? "";
  }, [getBookingText, description, depth, subRows, bookingTexts]);

  const lineItemNames = subRows
    .map((subRow) => subRow.original.originalName)
    .join(", ");

  const visibleText =
    bookingText === "loading" ? "" : bookingText || lineItemNames;

  const handleOnChange = async (value: string) => {
    const rowsIds = subRows.map((subRow) => subRow.original.id);

    setBookingText(rowsIds.join("-"), {
      lineItemIds: rowsIds,
      bookingText: value,
    });

    await onChange(rowsIds, value);
  };

  if (toolbarDisabled) {
    return (
      <div style={{ paddingLeft: `${depth * 2}rem` }}>
        <TextInputCell
          key={rowId}
          inputKey={rowId}
          value={visibleText}
          saveHandler={handleOnChange}
          tooltip={description}
          className={cn(className)}
          readOnly={readOnly}
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <div style={{ paddingLeft: `${depth * 2}rem` }}>
      <AssistantToolbarRoot openDelay={300}>
        <AssistantToolbarTrigger
          className="text-left overflow-hidden whitespace-nowrap text-ellipsis cursor-pointer"
          asChild={false}
        >
          <TextInputCell
            disabled={disabled}
            key={rowId}
            inputKey={rowId}
            value={visibleText}
            saveHandler={handleOnChange}
            tooltip={description}
            className={cn(className)}
            readOnly={readOnly}
          />
        </AssistantToolbarTrigger>
        <AssistantToolbar side="right">
          <AssistantToolbarAskAi
            aiPrompt={serviceResearchPrompt(
              description,
              companyInfo?.companyDescription ?? "not available",
              invoiceText,
            )}
            label={t("ask_ai.extras.service")}
            query={description}
          />
        </AssistantToolbar>
      </AssistantToolbarRoot>
    </div>
  );
};
